import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firestore';
import { doc, getDoc, updateDoc, setDoc, increment } from 'firebase/firestore';
import { IoCheckmarkCircleSharp } from 'react-icons/io5';
import { useUser } from '../context/userContext';

const WatchAds = () => {
  const [taskDetails, setTaskDetails] = useState(null);
  const [message, setMessage] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [isAdLoading, setIsAdLoading] = useState(false);
  const [isAdPlayed, setIsAdPlayed] = useState(false);
  const [buttonClicking, setButtonClicking] = useState(false);
  const [adsWatched, setAdsWatched] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isDailyLimitReached, setIsDailyLimitReached] = useState(false);
  
  const { id, setBalance } = useUser();

  const DAILY_AD_LIMIT = 30; // Changed from 20 to 30 as per your requirement
  const RESET_TIME_HOURS = 8;
  const zoneId = '9674745';
  const AD_REWARD_AMOUNT = taskDetails?.bonusAmount || 0;

  // Load user's watched ads count and check reset condition
  useEffect(() => {
    const fetchAdsData = async () => {
      const userRef = doc(db, 'telegramUsers', id);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const lastWatchTime = userData.lastAdWatchTime?.toDate();
        const now = new Date();
        
        // Check if 24 hours have passed since lastAdWatchTime
        if (lastWatchTime && ((now - lastWatchTime) >= (RESET_TIME_HOURS * 60 * 60 * 1000))) {
          await updateDoc(userRef, {
            adsWatchedToday: 0,
            lastAdWatchTime: null
          });
          setAdsWatched(0);
          setIsDailyLimitReached(false);
        } else {
          setAdsWatched(userData.adsWatchedToday || 0);
          
          // Set timer if limit reached
          if (userData.adsWatchedToday >= DAILY_AD_LIMIT && lastWatchTime) {
            const timePassed = (now - lastWatchTime) / (1000 * 60 * 60);
            const remainingTime = RESET_TIME_HOURS - timePassed;
            setTimeLeft(Math.floor(remainingTime * 3600));
            setIsDailyLimitReached(true);
          }
        }
      }
    };

    fetchAdsData();
  }, [id]);

  // Timer effect
  useEffect(() => {
    let timer;
    if (isDailyLimitReached && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft <= 0 && isDailyLimitReached) {
      setIsDailyLimitReached(false);
      setAdsWatched(0);
      // Reset in Firestore
      const resetAdsCount = async () => {
        const userRef = doc(db, 'telegramUsers', id);
        await updateDoc(userRef, {
          adsWatchedToday: 0,
          lastAdWatchTime: null
        });
      };
      resetAdsCount();
    }
    return () => clearInterval(timer);
  }, [timeLeft, isDailyLimitReached, id]);

  // Monetag SDK loader (unchanged)
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "//whephiwums.com/sdk.js";
    script.dataset.zone = zoneId;
    script.dataset.sdk = `show_${zoneId}`;
    script.async = true;

    script.onload = () => {
      console.log("Monetag SDK successfully loaded.");
    };

    script.onerror = () => {
      console.log("Error loading Monetag SDK.");
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Fetch task details (unchanged)
  useEffect(() => {
    const fetchTaskDetails = async () => {
      const taskRef = doc(db, 'Tasks', 'task-1');
      const taskSnap = await getDoc(taskRef);

      if (taskSnap.exists()) {
        const taskData = taskSnap.data();
        setTaskDetails(taskData);
      } else {
        setMessage('Error: Task not found.');
      }
    };

    fetchTaskDetails();
  }, []);

  const loadMonetagAd = async () => {
    if (window.show_9674745) {
      setIsAdLoading(true);

      try {
        await window.show_9674745();

        const bonus = parseInt(taskDetails.bonusAmount) || 0;
        const userRef = doc(db, 'telegramUsers', id);
        const newAdsWatched = adsWatched + 1;

        // Update ads watched count and last watch time (only when reaching limit)
        await updateDoc(userRef, {
          balance: increment(bonus),
          taskPoints: increment(bonus),
          adsWatchedToday: newAdsWatched,
          lastAdWatchTime: newAdsWatched >= DAILY_AD_LIMIT ? new Date() : null
        });

        setBalance(prevBalance => prevBalance + bonus);
        setAdsWatched(newAdsWatched);

        // Check if daily limit reached
        if (newAdsWatched >= DAILY_AD_LIMIT) {
          setIsDailyLimitReached(true);
          setTimeLeft(RESET_TIME_HOURS * 3600);
        }

        setModalMessage(
          <div className="w-full flex justify-center flex-col items-center space-y-3">
            <div className="w-full items-center justify-center flex flex-col space-y-2">
              <IoCheckmarkCircleSharp size={32} className="text-accent" />
              <p className='font-medium text-center'>Let's go!!</p>
            </div>
            <h3 className="font-medium text-[20px] text-[#ffffff] pt-2 pb-2">
              <span className="text-accent">+{bonus}</span> Voscoin CLAIMED
            </h3>
            <p className="pb-6 text-[15px] w-full text-center">
              Reward has been added to your main balance successfully!
            </p>
          </div>
        );

        setIsAdLoading(false);
        setIsAdPlayed(true);
        setShowMessage(true);
      } catch (error) {
        console.error("Error updating balance:", error);
        setIsAdLoading(false);
        setMessage('Error: Failed to update balance');
      }
    } else {
      setMessage('Monetag SDK not found.');
    }
  };

  const handleWatchAd = async () => {
    if (isDailyLimitReached) return;

    setButtonClicking(true);
    setTimeout(() => {
      setButtonClicking(false);
    }, 500);

    if (!taskDetails) return;

    await loadMonetagAd();
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  return (
      <div className="w-full max-w-md">
    <div className="watch-ads-container">
      {taskDetails ? (
        <>
          {/* Watch Ads Section - Updated Design */}
          <div 
            className={`w-full rounded-lg p-3 backdrop-blur-md border ${isDailyLimitReached ? 'bg-white bg-opacity-10 cursor-not-allowed' : 'bg-white bg-opacity-20 cursor-pointer active:bg-opacity-20 border-[#0f0c07] border-opacity-20'}`}
            onClick={!isDailyLimitReached ? handleWatchAd : undefined}
          >
            <div className="flex justify-between items-center">
              {/* Left side - Main text */}
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-medium text-sm">Per Ads {AD_REWARD_AMOUNT} <span className="text-[#a67928]">Voscoin</span></h3>
                </div>
                <p className="text-white text-opacity-70 text-xs mt-1">
                  Watch ad
                </p>
                {isDailyLimitReached && (
                  <p className="text-xs text-[#ffa400] mt-1">
                    limit reached. Next Available in {formatTime(timeLeft)}
                  </p>
                )}
              </div>

              {/* Right side - Counter and play icon */}
              <div className="flex items-center gap-2">
                <span className="text-xs">
                  <span className="text-[#ffa400]">{adsWatched}</span>
                  <span className="text-white">/{DAILY_AD_LIMIT}</span>
                </span>

                {/* Play button with gold background and white icon */}
                <div className={`p-1.5 rounded-full flex items-center justify-center ${isDailyLimitReached ? 'bg-gray-500' : 'bg-[#a67928]'}`}>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="white"
                  >
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {isAdLoading && !isAdPlayed && (
            <div className="mt-4 text-center text-white">
              <p>Loading ad... Please wait!</p>
            </div>
          )}

          {showMessage && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-[#333] p-8 rounded-lg max-w-sm w-full">
                {modalMessage}
                <button 
                  onClick={handleCloseMessage} 
                  className="mt-4 px-4 py-2 bg-[#99b8bd] text-black rounded"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-white">Loading task...</p>
      )}
    </div>
    </div>
  );
};

export default WatchAds;